/** Extract unique benchmark filter options from PC/TCP API rows. */

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.map((v) => String(v || '').trim()).filter(Boolean))).sort(
    (a, b) => a.localeCompare(b),
  );
}

export interface TcpBenchmarkFilterOptions {
  clients: string[];
  vessels: string[];
  segments: string[];
  operations: string[];
  yards: string[];
  builtDateMin: string | null;
  builtDateMax: string | null;
}

export interface PcBenchmarkFilterOptions {
  clients: string[];
  vessels: string[];
  segments: string[];
  ports: string[];
}

export interface BenchmarkSegmentOption {
  key: string;
  value: string[];
}

/** Normalize segment labels for dedup (case, spacing, slashes, brackets). */
export function benchmarkSegmentDedupKey(segment: string): string {
  return segment
    .trim()
    .toLowerCase()
    .replace(/[\s/]+/g, '')
    .replace(/[()]/g, '');
}

/**
 * Segment spellings that map to the same benchmark group.
 * API rows and dropdown keys may use any alias in the group.
 */
const BENCHMARK_SEGMENT_ALIAS_GROUPS: readonly string[][] = [
  ['MR', 'MR2', 'mr', 'mr2', 'Medium Range', 'medium range', 'MEDIUM RANGE'],
  ['Handy', 'Handysize', 'handy', 'HANDY', 'Handy (1-6W)', 'Handy (1-9W)'],
  ['Aframax', 'aframax', 'LR2/Aframax', 'LR2 / Aframax', 'LR2', 'lr2'],
  ['Panamax', 'panamax', 'LR1/Panamax', 'LR1 / Panamax', 'LR1', 'lr1'],
  ['Suezmax', 'suezmax', 'SUEZMAX'],
  ['VLCC', 'vlcc'],
  ['Intermediate', 'intermediate'],
];

export function resolveBenchmarkSegmentAliasGroup(segment: string): string[] | null {
  const key = benchmarkSegmentDedupKey(segment);
  if (!key) {
    return null;
  }
  for (const group of BENCHMARK_SEGMENT_ALIAS_GROUPS) {
    if (group.some((alias) => benchmarkSegmentDedupKey(alias) === key)) {
      return [...group];
    }
  }
  return null;
}

export function benchmarkSegmentKeysMatch(a: string, b: string): boolean {
  if (!a || !b) {
    return false;
  }
  if (a.trim().toLowerCase() === b.trim().toLowerCase()) {
    return true;
  }
  const groupA = resolveBenchmarkSegmentAliasGroup(a);
  const groupB = resolveBenchmarkSegmentAliasGroup(b);
  if (groupA && groupB) {
    return benchmarkSegmentDedupKey(groupA[0]) === benchmarkSegmentDedupKey(groupB[0]);
  }
  return benchmarkSegmentDedupKey(a) === benchmarkSegmentDedupKey(b);
}

function canonicalBenchmarkSegmentKey(segment: string): string {
  const trimmed = segment.trim();
  if (!trimmed) {
    return trimmed;
  }
  const aliases = resolveBenchmarkSegmentAliasGroup(trimmed);
  if (aliases) {
    const shortCodes = aliases.filter((alias) => /^[A-Z0-9]{2,5}$/i.test(alias));
    if (shortCodes.length) {
      return shortCodes
        .map((alias) => alias.toUpperCase())
        .sort((a, b) => a.length - b.length || a.localeCompare(b))[0];
    }
    return aliases[0];
  }
  if (/^[a-z0-9]{2,6}$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return trimmed;
}

function benchmarkSegmentGroupKey(segment: string): string {
  const aliases = resolveBenchmarkSegmentAliasGroup(segment);
  if (aliases) {
    return benchmarkSegmentDedupKey(canonicalBenchmarkSegmentKey(aliases[0]));
  }
  return benchmarkSegmentDedupKey(segment);
}

function pickPreferredBenchmarkSegmentKey(a: string, b: string): string {
  const left = canonicalBenchmarkSegmentKey(a);
  const right = canonicalBenchmarkSegmentKey(b);
  if (benchmarkSegmentKeysMatch(left, right)) {
    if (/^[A-Z0-9]{2,5}$/.test(left)) {
      return left;
    }
    if (/^[A-Z0-9]{2,5}$/.test(right)) {
      return right;
    }
    return left;
  }
  return left;
}

function mergeBenchmarkSegmentOption(
  existing: BenchmarkSegmentOption | undefined,
  incoming: BenchmarkSegmentOption,
): BenchmarkSegmentOption {
  const aliasValues = resolveBenchmarkSegmentAliasGroup(incoming.key) ?? [];
  const mergedValues = uniqueSorted([
    ...(existing?.value ?? []),
    ...incoming.value,
    incoming.key,
    ...aliasValues,
  ]);
  const key = existing
    ? pickPreferredBenchmarkSegmentKey(existing.key, incoming.key)
    : canonicalBenchmarkSegmentKey(incoming.key);
  return { key, value: mergedValues };
}

/** Parse ship built dates from TCP benchmark API rows (multiple formats). */
export function parseBenchmarkBuiltDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const text = String(value)
    .trim()
    .replace(/,(\d)/, ', $1');
  if (/^\d{4}$/.test(text)) {
    return new Date(Number(text), 0, 1);
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseBuiltDateForInput(value: unknown): Date | null {
  return parseBenchmarkBuiltDate(value);
}

function toInputDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function resolveTcpBenchmarkRows(data: unknown): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  const record = data as Record<string, unknown>;
  if (Array.isArray(record['matching_data'])) return record['matching_data'] as any[];
  if (Array.isArray(record['data'])) return record['data'] as any[];
  return [];
}

export function resolvePcBenchmarkRows(data: unknown): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  const record = data as Record<string, unknown>;
  if (Array.isArray(record['matching_data'])) return record['matching_data'] as any[];
  if (Array.isArray(record['data'])) return record['data'] as any[];
  return [];
}

/** Normalize benchmark ports API `ports` object/array to unique display labels. */
export function extractBenchmarkPortNamesFromApi(ports: unknown): string[] {
  const candidates: string[] = [];

  if (Array.isArray(ports)) {
    ports.forEach((item) => {
      if (typeof item === 'string') {
        candidates.push(item);
        return;
      }
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        const label = String(
          record['name'] ?? record['port'] ?? record['port_name'] ?? record['value'] ?? '',
        ).trim();
        if (label) {
          candidates.push(label);
        }
      }
    });
  } else if (ports && typeof ports === 'object') {
    const record = ports as Record<string, unknown>;
    Object.keys(record).forEach((key) => {
      const trimmedKey = key.trim();
      if (trimmedKey) {
        candidates.push(trimmedKey);
      }
      const aliases = record[key];
      if (Array.isArray(aliases)) {
        aliases.forEach((alias) => {
          const label = String(alias || '').trim();
          if (label) {
            candidates.push(label);
          }
        });
      }
    });
  }

  const byKey = new Map<string, string>();
  for (const raw of candidates) {
    const label = raw.trim();
    if (!label) {
      continue;
    }
    const dedup = label.toLowerCase();
    const existing = byKey.get(dedup);
    if (!existing || isPreferredBenchmarkPortLabel(label, existing)) {
      byKey.set(dedup, label);
    }
  }

  return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
}

function isPreferredBenchmarkPortLabel(candidate: string, existing: string): boolean {
  const candidateTitle =
    candidate[0] === candidate[0]?.toUpperCase() &&
    candidate.slice(1) !== candidate.slice(1).toUpperCase();
  const existingTitle =
    existing[0] === existing[0]?.toUpperCase() &&
    existing.slice(1) !== existing.slice(1).toUpperCase();
  if (candidateTitle && !existingTitle) {
    return true;
  }
  if (!candidateTitle && existingTitle) {
    return false;
  }
  return candidate.localeCompare(existing) < 0;
}

/**
 * Normalize segment dropdown options.
 * - Object form `{ key, value[] }` from the Segment API: display `key`, keep `value` exactly.
 * - String tokens (local/catalog fallback): build a key/value group without inventing aliases
 *   beyond the provided token itself.
 */
export function normalizeBenchmarkSegmentOptions(
  segments: unknown[],
  fallback = 'MR',
): BenchmarkSegmentOption[] {
  if (!segments?.length) {
    return [{ key: fallback, value: [fallback] }];
  }

  const parsed = segments
    .map((segment) => {
      if (typeof segment === 'string') {
        const key = segment.trim();
        if (!key) {
          return null;
        }
        return {
          key: canonicalBenchmarkSegmentKey(key),
          value: [key],
        };
      }
      if (segment && typeof segment === 'object') {
        const record = segment as Record<string, unknown>;
        const key = String(record['key'] ?? record['name'] ?? record['segment'] ?? '').trim();
        if (!key) {
          return null;
        }
        // Preserve API value array order/contents exactly — key is UI-only.
        if (Array.isArray(record['value'])) {
          const exactValues = (record['value'] as unknown[])
            .map((item) => String(item ?? '').trim())
            .filter(Boolean);
          return {
            key,
            value: exactValues.length ? exactValues : [key],
          };
        } else if (record['value'] !== undefined && record['value'] !== null) {
          return {
            key,
            value: [String(record['value']).trim()],
          };
        }
        return {
          key,
          value: [key],
        };
      }
      return null;
    })
    .filter((item): item is BenchmarkSegmentOption => !!item);

  const byKey = new Map<string, BenchmarkSegmentOption>();
  for (const option of parsed) {
    const mapKey = option.key.trim().toLowerCase();
    const existing = byKey.get(mapKey);
    if (!existing) {
      byKey.set(mapKey, { key: option.key, value: [...option.value] });
      continue;
    }
    // Prefer the latest explicit value array when keys collide (API overwrites catalog).
    byKey.set(mapKey, {
      key: option.key,
      value: option.value.length ? [...option.value] : [...existing.value],
    });
  }

  return Array.from(byKey.values()).sort((a, b) => a.key.localeCompare(b.key));
}

export function extractTcpBenchmarkFilterOptions(
  rows: any[],
  root?: Record<string, unknown>,
): TcpBenchmarkFilterOptions {
  const rootClients = coerceRootList(root?.['client'] ?? root?.['clients']);
  const rootOperations = coerceRootList(
    root?.['port_consumption_parameter'] ?? root?.['operations'],
  );
  const rootSegments = coerceRootList(root?.['vessel_class'] ?? root?.['segments']);

  const clients =
    rootClients.length > 0
      ? rootClients
      : uniqueSorted(rows.map((r) => String(r?.client || '').trim()).filter(Boolean));

  const vessels = uniqueSorted(rows.map((r) => String(r?.vessel_name || '')));
  const segments =
    rootSegments.length > 0
      ? rootSegments
      : uniqueSorted(
          rows.map((r) => String(r?.vessel_class || r?.segment || '')),
        );
  const operations =
    rootOperations.length > 0
      ? rootOperations
      : uniqueSorted(rows.map((r) => String(r?.port_consumption_parameter || '')));
  const yards = uniqueSorted(
    rows.map((r) =>
      String(r?.yard || r?.yard_name || r?.ship_yard || '').trim(),
    ),
  );

  const builtDates = rows
    .flatMap((r) => [r?.ship_built_from, r?.ship_built_to])
    .map(parseBuiltDateForInput)
    .filter((d): d is Date => !!d);

  let builtDateMin: string | null = null;
  let builtDateMax: string | null = null;
  if (builtDates.length) {
    const min = new Date(Math.min(...builtDates.map((d) => d.getTime())));
    const max = new Date(Math.max(...builtDates.map((d) => d.getTime())));
    builtDateMin = toInputDateString(min);
    builtDateMax = toInputDateString(max);
  }

  return {
    clients,
    vessels,
    segments,
    operations,
    yards,
    builtDateMin,
    builtDateMax,
  };
}

export function extractPcBenchmarkFilterOptions(rows: any[]): PcBenchmarkFilterOptions {
  return {
    clients: uniqueSorted(rows.map((r) => String(r?.client || ''))),
    vessels: uniqueSorted(rows.map((r) => String(r?.vessel_name || ''))),
    segments: uniqueSorted(
      rows.map((r) => String(r?.vessel_class || r?.segment || '')),
    ),
    ports: uniqueSorted(rows.map((r) => String(r?.port_name || r?.port || ''))),
  };
}

function coerceRootList(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return uniqueSorted(
      value.map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>;
          return String(
            record['client'] ??
              record['client_name'] ??
              record['name'] ??
              record['value'] ??
              '',
          );
        }
        return String(item ?? '');
      }),
    );
  }
  if (typeof value === 'string') {
    return value.trim() ? [value.trim()] : [];
  }
  return [];
}
